/* eslint-disable react/no-unknown-property */
import React from 'react';
import { Pictogram } from '@doveit/bricks';
import {
  UserPermissionGroup,
  UserPermissions,
} from './types';
import { useCurrentAgentSync } from '../../hooks/use-agent/useAgent';
import useRBAC from '../../hooks/use-rbac/useRBAC';
import { permissionFactories, PermissionFactory } from './permissionsFactories';
import * as styles from './UserPermissionsContext.style';
import GenericErrorPage from '../../pages/errors/generic/GenericErrorPage';

interface PermissionCacheEntry {
  context: {
    rbac: ReturnType<typeof useRBAC>;
    currentAgent: ReturnType<typeof useCurrentAgentSync>['data'];
  };
  value: unknown;
}

export interface UserPermissionsContextValue {
  getPermissions: <T extends UserPermissionGroup>(
    group: T,
  ) => UserPermissions[T];
}

const UserPermissionsContext = React.createContext<UserPermissionsContextValue | undefined>(
  undefined,
);

interface UserPermissionsProviderProps {
  children: React.ReactNode;
}

export function UserPermissionsProvider({
  children,
}: UserPermissionsProviderProps) {
  const rbac = useRBAC();

  const {
    data: currentAgent,
    isLoading: isCurrentAgentLoading,
    errors: currentAgentErrors,
  } = useCurrentAgentSync();

  const isLoading = rbac.loading || isCurrentAgentLoading;
  const error = rbac.error || !!currentAgentErrors;

  const permissionContext = React.useMemo(
    () => ({ rbac, currentAgent }),
    [rbac, currentAgent],
  );

  const cache = React.useRef(new Map<UserPermissionGroup, PermissionCacheEntry>());

  const getPermissions = React.useCallback(
    <T extends UserPermissionGroup>(
      group: T,
    ): UserPermissions[T] => {
      const cached = cache.current.get(group);

      if (cached?.context === permissionContext) {
        return cached.value as UserPermissions[T];
      }

      const factory = permissionFactories[group] as PermissionFactory<T>;

      const permissions = factory(
        rbac,
        currentAgent,
      );

      cache.current.set(group, {
        context: permissionContext,
        value: permissions,
      });

      return permissions;
    },
    [rbac, currentAgent, permissionContext],
  );

  const contextValue = React.useMemo(
    () => ({
      getPermissions,
    }),
    [getPermissions],
  );

  if (isLoading) {
    return (
      <div
        css={styles.wrapper}
        aria-label="Permessi utente in caricamento"
      >
        <div css={styles.loading}>
          <Pictogram />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div css={styles.wrapper}>
        <GenericErrorPage title="Impossibile caricare la pagina." />
      </div>
    );
  }

  return (
    <UserPermissionsContext.Provider value={contextValue}>
      {children}
    </UserPermissionsContext.Provider>
  );
}

export function useUserPermissionsContext(): UserPermissionsContextValue {
  const context = React.useContext(UserPermissionsContext);

  if (context === undefined) {
    throw new Error(
      'useUserPermissionsContext must be used within a UserPermissionsProvider',
    );
  }

  return context;
}

export default UserPermissionsContext;
